'use client'

import React, { useCallback, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { Anchor, Button, Flex, Text } from '@mantine/core'
import { Audience } from '@phoenix-web/utils/journey'
import { authApiPaths, COURTYARD2_AUTH_SIGNUP_PATH_HOMEBUYER } from '@/constants'
import { onAuthLoginClick } from '@/lib/error-logger'
import { blendPagesContent } from '@/text'

export enum BlendCtaType {
  LOGIN = 'login',
  SIGNUP = 'signup',
}

interface BlendCtaProps {
  ctaType?: BlendCtaType
}

export const BlendCtaSection = ({ ctaType = BlendCtaType.SIGNUP }: BlendCtaProps) => {
  const router = useRouter()

  const login = useCallback(
    (e: React.SyntheticEvent) => {
      onAuthLoginClick()
      router.push(
        `${authApiPaths.LOGIN}?returnTo=${encodeURIComponent(window.location.origin + window.location.pathname + window.location.search)}`
      )
    },
    [router]
  )

  const signup = useCallback(() => {
    const search = window.location.search
      ? `${window.location.search}&audience=${Audience.HOMEBUYER}`
      : `?audience=${Audience.HOMEBUYER}`
    const returnTo =
      typeof window !== 'undefined' ? window.location.origin + window.location.pathname + search : undefined
    router.push(
      `${COURTYARD2_AUTH_SIGNUP_PATH_HOMEBUYER}${returnTo ? '&returnTo=' + encodeURIComponent(returnTo) : ''}`
    )
  }, [router])

  const blendContentMap = useMemo(
    () => ({
      login: {
        onCtaClick: login,
        ...blendPagesContent.login,
        altCtaOnClick: signup,
      },
      signup: {
        onCtaClick: signup,
        ...blendPagesContent.signup,
        altCtaOnClick: login,
      },
    }),
    [login, signup]
  )

  const { onCtaClick, ctaButtonText, altCtaText, altCtaLinkLabel, altCtaOnClick } = blendContentMap[ctaType]
  return (
    <>
      <Flex data-testid="staging-cta-main" justify={{ base: 'center', sm: 'flex-start' }}>
        <Button
          data-testid="staging-cta-primary"
          size="md"
          radius="md"
          maw="350px"
          mb="xs"
          w={{ base: '100%', sm: 'fit-content' }}
          miw={{ base: '100%', sm: '245px' }}
          onClick={onCtaClick}
        >
          {ctaButtonText}
        </Button>
      </Flex>
      <Text ta="left">
        {altCtaText}{' '}
        <Anchor fw={700} data-testid="staging-cta-secondary" onClick={altCtaOnClick}>
          {altCtaLinkLabel}
        </Anchor>
      </Text>
    </>
  )
}
